// src/api/api.js

export const BASE_URL = "https://sweetplateapi.sweetplate.org"; 
// export const BASE_URL = "http://localhost:32211"; 

async function request(path, { method = "GET", body, headers = {}, withAuth = false } = {}) {
  const finalHeaders = {
    accept: "*/*",
    "Content-Type": "application/json",
    ...headers,
  };

  // ✅ Attach token if required
  if (withAuth) {
    const token = localStorage.getItem("sp_token");
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    
    // credentials: "include",
    //cache: "no=store",
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  const text = await res.text();
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const err = new Error((data && data.message) || res.statusText || "Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Multipart/form-data helper
*/
async function multipart(path, formData, { method = "POST", headers = {}, withAuth = false } = {}) {
  const finalHeaders = {
    accept: "*/*",
    ...headers,
  };
  
  // ✅ Attach token if required
  if (withAuth) {
    const token = localStorage.getItem("sp_token");
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    //cache: "no-store",
    // credentials: "include",
    body: formData,
  });

  let data = null;
  const text = await res.text();
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const err = new Error((data && data.message) || res.statusText || "Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}


export const api = {
  // -------- Auth --------
  auth: {
    // POST /api/splate/auth/login
    login(email, password) {
      return request("/api/splate/auth/login", {
        method: "POST",
        body: { email, password },
      });
    },

    // --------Logout--------
    // POST /api/splate/auth/logout
    logout() {
      return request("/api/splate/auth/logout", {
        method: "POST",
        withAuth: true,
      });
    },

    // -------- Password Reset Flow --------
    // POST /api/splate/auth/reset/send-otp
    sendResetOtp(email) {
      return request("/api/splate/auth/reset/send-otp", {
        method: "POST",
        body: { email },
      });
    },

    // POST /api/splate/auth/reset/verify-otp
    verifyResetOtp(email, otp) {
      return request("/api/splate/auth/reset/verify-otp", {
        method: "POST",
        body: { email, otp },
      });
    },

    // POST /api/splate/auth/reset/set-password
    setNewPassword(email, password) {
      return request("/api/splate/auth/reset/set-password", {
        method: "POST",
        body: { email, password },
      });
    },

    // -------- SignUp Flow --------
    // POST /api/splate/auth/send-otp
    sendOtp(email) {
      return request("/api/splate/auth/send-otp", {
        method: "POST",
        body: { email },
      });
    },

    // POST /api/splate/auth/verify-otp
    verifyOtp(email, otp) {
      return request("/api/splate/auth/verify-otp", {
        method: "POST",
        body: { email, otp },
      });
    },

    // POST /api/splate/auth/set-password
    setPassword(email, password, referralCode, role, latitude, longitude) {
      const body = { email, password, latitude, longitude};
      if (referralCode) body.referralCode = referralCode;
      if (role) body.role = role;
      return request("/api/splate/auth/set-password", {
        method: "POST",
        body,
      });
    },
  },

  // ------- Withdrawal (Vendor & Rider Earnings) -------
withdrawal: {
  create({ amount, bankCode, accountNumber, orderIds }) {
    return request("/api/splate/withdraw", {
      method: "POST",
      withAuth: true,
      body: { amount, bankCode, accountNumber, orderIds },
    });
  },
  
  // GET user withdrawal history
  listMine() {
    return request("/api/splate/withdraw/mine", {
      method: "GET",
      withAuth: true,
    });
  },

  // GET user withdrawal history
  balance() {
    return request("/api/splate/withdraw/wallet", {
      method: "GET",
      withAuth: true,
    });
  },
},

// ------- Withdrawal (Vendor & Rider Earnings) -------
bank: {
  // GET user withdrawal history
  list() {
    return request("/api/splate/banks", {
      method: "GET",
      withAuth: false,
    });
  },
},

location: {
  // GET user withdrawal history
  allowedCities() {
    return request("/api/splate/allowed-cities", {
      method: "GET",
      withAuth: false,
    });
  },
},

// -------- User profile detail --------
profile: {
  // GET /api/splate/auth/me
  viewProfile() {
    return request("/api/splate/auth/me", { method: "GET", withAuth: true });
  },
  updateProfileInfo(payload) {
    return request("/api/splate/auth/update-profile", {
      method: "PUT",
      body: payload,
      withAuth: true,
    });
  },
  },

  // -------- Categories --------
  categories: {
    // GET /api/splate/categories
    list() {
      // Backend returns a plain array
      return request("/api/splate/categories", { method: "GET" });
    },

    // POST /api/splate/categories
    create(name, description = "") {
      return request("/api/splate/categories", {
        method: "POST",
        body: { name, description },
        withAuth: true,
      });
    },
  },

  // -------- Products --------
  products: {
    // GET /api/splate/products
    listAll() {
      return request("/api/splate/products", { method: "GET" });
    },

    // GET /api/splate/products/user  (requires auth)
    listMine() {
      return request("/api/splate/products/user", {
        method: "GET",
        withAuth: true,
      });
    },

    // GET /api/splate/products/featured?type=top_selling|top_farmer|flash_sale
    listFeatured(type) {
      const q = type ? `?type=${encodeURIComponent(type)}` : "";
      return request(`/api/splate/products/featured${q}`, { method: "GET" });
    },

    // GET /api/splate/products/category/:categoryId
    listByCategory(categoryId) {
      return request(`/api/splate/products/category/${categoryId}`, {
        method: "GET",
      });
    },

    // GET /api/splate/products/:productId
    getOne(productId) {
      return request(`/api/splate/products/${productId}`, { method: "GET" });
    },

    // POST /api/splate/products  (multipart; requires auth)
    create({
      name,
      stockQuantity,
      pricePerUnit,
      weight, 
      category, 
      visibility, 
      description, 
      productType, 
      discountPercent, 
      images, 
      sellerName,
      sellerPhone,
    }) {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("stockQuantity", String(stockQuantity));
      fd.append("pricePerUnit", String(pricePerUnit));
      if (weight != null && weight !== "") fd.append("weight", String(weight));
      fd.append("category", category);
      fd.append("visibility", visibility);
      if (description) fd.append("description", description);
      if (productType) fd.append("productType", productType);
      if (discountPercent != null && discountPercent !== "") {
        fd.append("discountPercent", String(discountPercent));
      }
      if (sellerName)  fd.append("sellerName", sellerName);
      if (sellerPhone) fd.append("sellerPhone", sellerPhone);
      (images || []).forEach((file) => fd.append("images", file));

      return multipart("/api/splate/products", fd, {
        method: "POST",
        withAuth: true,
      });
    },

    // PUT /api/splate/products/:productId  (multipart; requires auth)
    update(productId, fields = {}, newImages = [], replaceImages = false) {
      const formData = new FormData();
      
      // Only proceed if there's actually something to update
      const hasChanges =
      Object.keys(fields).length > 0 ||
      (Array.isArray(newImages) && newImages.length > 0);
      
      if (!hasChanges) {
        return Promise.resolve({ message: "No changes to save" });
      }
      // Add fields
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, typeof value === "number" ? String(value) : value);
        }
      });
      // Always include flag when images are involved
      formData.append("replaceImages", replaceImages ? "true" : "false");

      // Add images
      newImages?.forEach((file) => {
        if (file instanceof File) {
          formData.append("images", file);
        }
      });
      
      return multipart(`/api/splate/products/${productId}`, formData, {
        method: "PUT",
        withAuth: true,
      });
    },

    // DELETE /api/splate/products/:productId  (requires auth)
    remove(productId) {
      return request(`/api/splate/products/${productId}`, {
        method: "DELETE",
        withAuth: true,
      });
    },
  },

  // -------- Orders --------
  orders: {
    // POST /api/splate/orders
    place({ items, paymentMethod, shippingAddressId, shippingAddress, buyerPhone }) {
      const body = { items, paymentMethod };
      if (shippingAddressId) body.shippingAddressId = shippingAddressId;
      if (shippingAddress) body.shippingAddress = shippingAddress;
      if (buyerPhone)        body.buyerPhone        = buyerPhone;
      return request("/api/splate/orders", { method: "POST", body, withAuth: true });
    },

    // BUYER: GET /api/splate/orders/my-orders
    mine() {
      return request("/api/splate/orders/my-orders", { method: "GET", withAuth: true });
    },

    // BUYER: GET /api/splate/orders/:orderId
    getOne(orderId) {
      return request(`/api/splate/orders/${encodeURIComponent(orderId)}`, { method: "GET", withAuth: true });
    },

    // VENDOR: GET /api/splate/orders/vendor/my-orders
    vendorMine() {
      return request("/api/splate/orders/vendor/my-orders", { method: "GET", withAuth: true });
    },

    // VENDOR: GET /api/splate/orders/vendor/:orderId
    vendorGetOne(orderId) {
      return request(`/api/splate/orders/vendor/${encodeURIComponent(orderId)}`, { method: "GET", withAuth: true });
    },

    // PATCH /api/splate/orders/:orderId/status
    updateStatus(orderId, nextStatus, extras = {}) {
      // Allow either a raw status string or an object
      const body =
        typeof nextStatus === "object" && nextStatus.to
          ? { to: nextStatus.to }
          : { to: nextStatus };

      if (extras.assignedRiderId) body.assignedRiderId = extras.assignedRiderId;

      return request(`/api/splate/orders/${encodeURIComponent(orderId)}/status`, {
        method: "PATCH",
        body,
        withAuth: true,
      });
    },
 
    transitionCheck(orderId, to) {
      return request(`/api/splate/orders/${orderId}/status?dryRun=1`, {
        method: "PATCH",
        body: { to },
        withAuth: true, 
      });
    },
    transition(orderId, to) {
      return request(`/api/splate/orders/${orderId}/status`, {
        method: "PATCH",
        body: { to },
        withAuth: true, 
      });
    }
  },

  // -------- Cart --------
  cart: {
  // GET /api/splate/cart
  view() {
    return request("/api/splate/cart", { method: "GET", withAuth: true });
  },
  // POST /api/splate/cart
  add(productId, quantity = 1) {
    return request("/api/splate/cart", {
      method: "POST",
      withAuth: true,
      body: { productId, quantity },
    });
  },
  // PUT /api/splate/cart
  update(productId, quantity) {
    return request("/api/splate/cart", {
      method: "PUT",
      withAuth: true,
      body: { productId, quantity },
    });
  },
  // DELETE /api/splate/cart
  clear() {
    return request("/api/splate/cart", { method: "DELETE", withAuth: true });
   },
  },

  // ------- KYC -------
  kyc: {
    getStatus() {
      return request("/api/splate/kyc/me", { withAuth: true });
    },
    uploadBusinessLicense(file) {
      const form = new FormData();
      form.append("businessLicense", file);
      return fetch(`${BASE_URL}/api/splate/kyc/business-license`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("sp_token")}` },
        body: form,
      }).then(async r => {
        const t = await r.text(); let d; try { d = JSON.parse(t) } catch { d = t }
        if (!r.ok) { const e = new Error(d?.message || "Upload failed"); e.status=r.status; e.data=d; throw e; }
        return d;
      });
    },
    uploadAddressProof(file) {
      const form = new FormData();
      form.append("addressProof", file);
      return fetch(`${BASE_URL}/api/splate/kyc/address-proof`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("sp_token")}` },
        body: form,
      }).then(async r => {
        const t = await r.text(); let d; try { d = JSON.parse(t) } catch { d = t }
        if (!r.ok) { const e = new Error(d?.message || "Upload failed"); e.status=r.status; e.data=d; throw e; }
        return d;
      });
    },
    updateLocation(payload) {
    return request("/api/splate/auth/update-location", {
      method: "PUT",
      body: payload,
      withAuth: true,
    });
  },
    finalize({ phoneNumber, docType, country }) {
      return request("/api/splate/kyc/finalize", {
        method: "POST",
        body: { phoneNumber, docType, country },
        withAuth: true,
      });
    },
  },

  // ------- Onboarding -------
  onboarding: {
    saveProductPayment({ category, shipping, bankName, bankAccount, feePayment }) {
      return request("/api/splate/onboarding/product-payment", {
        method: "POST",
        body: { category, shipping, bankName, bankAccount, feePayment },
        withAuth: true,
      });
    },
    saveSecurity({ twoFAEnabled, termsAccepted }) {
      return request("/api/splate/onboarding/security", {
        method: "POST",
        body: { twoFAEnabled, termsAccepted },
        withAuth: true,
      });
    },
  },

  // ------- Delivery -------
  riders: {
    getRiders() {
      return request("/api/splate/riders", { withAuth: true });
    },
  },

  // ------- Delivery -------
  delivery: {
    getMy() {
      return request("/api/splate/delivery/settings/me", { withAuth: true });
    },
    save(payload) {
      return request("/api/splate/delivery/settings", {
        method: "POST",
        body: payload,
        withAuth: true,
      });
    },
  },

  // ------- Business Upload -------
  business: {
  createProfile(payload) {
    return request("/api/splate/business-profile", {
      method: "POST",
      body: payload,
      withAuth: true,
    });
  },

  setup(formData) {
      return multipart("/api/splate/shop/setup", formData, {
        method: "POST",
        withAuth: true,
      });
    },
    getStore() {
      return request("/api/splate/shop/user", { withAuth: true });
    },
  },

  // -------- Notifications --------
  notifications: {
    // POST /api/splate/notifications/save-token
    saveToken(userId, token) {
      return request("/api/splate/notifications/save-token", {
        method: "POST",
        body: { userId, token },
        withAuth: true,
      });
    },

    // POST /api/splate/notifications/order
    sendOrderNotification(userId, orderId, status = "") {
      const body = { userId, orderId };
      if (status) body.status = status;
      return request("/api/splate/notifications/order", {
        method: "POST",
        body,
        withAuth: true,
      });
    },
  },

};

export default api;
